import { create } from 'zustand'
import type {
  MeetingRecord,
  MeetingStatus,
  MeetingType,
} from '../models/meeting.model'
import { meetingsService, resolveStatus, getColors } from '../services/meetings.service'
import { meetingsRepo } from '../services/meetings.repo'
import { meetingsApi } from '../services/meetings.api'
import { matchesMeetingId } from '../utils/recurrence'
import { useAuthStore } from '@/features/auth/store/auth.store'

/**
 * Reactive cache over `meetingsService`. The repo (localStorage) is the
 * source of truth; this store hydrates from it once and refreshes on every
 * mutation. All UI components should subscribe via selectors and call the
 * named actions — never reach into the repo directly.
 *
 * Legacy callers (InstantMeetingDialog/JoinMeetingDialog/ScheduleMeeting)
 * that used `addMeeting(record)` directly keep working because the action
 * still accepts a full record and forwards to `meetingsRepo.insert`.
 */

// Keep the type alias the rest of the app already imports.
export type Meeting = MeetingRecord
export type { MeetingStatus, MeetingType }
export { getColors, resolveStatus }

interface MeetingsState {
  meetings: Meeting[]
  hydrated: boolean
  loading: boolean

  hydrate: () => void
  refresh: () => void
  fetchFromApi: () => Promise<void>
  /** Fetch only meetings overlapping the [from, to] window (UTC epoch ms).
   *  Result is *merged* into the store (upsert) so other ranges already
   *  loaded aren't lost — used by the calendar when the visible window
   *  changes. */
  fetchRange: (opts: { from?: number; to?: number }) => Promise<void>
  addMeeting: (meeting: Meeting) => void
  /** Show a meeting on this page without saving it. For guest joins — see the action. */
  seedMeeting: (meeting: Meeting) => void
  /** Drop every meeting, stored and on screen. Run on sign-out. */
  clear: () => void
  updateMeeting: (id: string, patch: Partial<Meeting>) => void
  updateMeetingStatus: (id: string, status: MeetingStatus) => void
  removeMeeting: (id: string) => void
  /** Delete on the server, then drop it locally. Throws if the call fails. */
  /** `scope: 'all'` on a day of a series cancels every day of it that has not happened yet. */
  deleteMeeting: (id: string, scope?: 'one' | 'all') => Promise<void>
  refreshStatuses: () => void
}

const reloadFromRepo = (): Meeting[] =>
  meetingsRepo.all().map((m) => ({
    ...m,
    status: resolveStatus(m.startTimeMs, m.endTimeMs),
  }))

/** Status is always derived from the times — the same rule `reloadFromRepo`
 *  applies, so a meeting keeps the same status however it reached the store. */
const withStatus = (m: Meeting): Meeting => ({
  ...m,
  status: resolveStatus(m.startTimeMs, m.endTimeMs),
})

/**
 * Match one meeting by either of the two ids it can be addressed by.
 *
 * The `!!id` guard is the whole point. Without it a nullish id made
 * `m.meetingId === id` true for every meeting that carries no `meetingId` —
 * and `m.id === id` true for every one with no `id` — so a call meant for a
 * single meeting silently hit the entire list. `updateMeeting` is what Mark
 * as Past goes through, which turned one meeting ending into every meeting
 * ending. The call sites reach this through `onMeetingClick(meeting: any)`,
 * so an absent id is not something the compiler would have caught.
 *
 * No id addresses no meeting.
 *
 * An occurrence of a series is matched by `id` only. Its `meetingId` is the
 * series code, shared by every occurrence, so matching on it would turn Mark
 * as Past on one day into every day of the series.
 */
const sameMeeting = matchesMeetingId

/**
 * Fill in the structural fields a partial meeting leaves out.
 *
 * Defensive: callers hand over legacy shapes and the guest-join previews, which
 * carry little more than an id, a title and a host.
 */
const withDefaults = (meeting: Meeting): Meeting => ({
  ...meeting,
  meetingId: meeting.meetingId ?? meeting.id,
  durationMinutes: meeting.durationMinutes ?? meeting.duration,
  description: meeting.description,
  timezone:
    meeting.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  invitees: meeting.invitees ?? [],
  participants: meeting.participants ?? [
    { id: `host_${meeting.id}`, name: meeting.host, role: 'host' },
  ],
  attachments: meeting.attachments ?? [],
  recurring: meeting.recurring ?? false,
  settings: meeting.settings ?? {
    passwordEnabled: false,
    waitingRoomEnabled: false,
    lockAfterStart: false,
    muteOnEntry: false,
    videoOnEntry: false,
    disableParticipantCamera: false,
    allowScreenShare: 'all',
    allowChat: true,
    allowReactions: true,
    allowParticipantRename: true,
    allowParticipantUnmute: true,
    autoRecord: false,
    transcriptionEnabled: false,
    allowJoinAnytime: false,
    allowLateJoin: true,
    requireApproval: false,
    reminderEnabled: false,
    reminderBeforeMinutes: 10,
    simulcast: false,
    sipEnabled: false,
    telepresence: false,
  },
  createdAt: meeting.createdAt ?? Date.now(),
  updatedAt: meeting.updatedAt ?? Date.now(),
})

export const useMeetingsStore = create<MeetingsState>((set, get) => ({
  meetings: typeof window === 'undefined' ? [] : reloadFromRepo(),
  hydrated: typeof window !== 'undefined',
  loading: false,

  hydrate: () => set({ meetings: reloadFromRepo(), hydrated: true }),

  refresh: () => set({ meetings: reloadFromRepo() }),

  fetchFromApi: async () => {
    set({ loading: true })
    try {
      const { meetings } = await meetingsApi.list()
      set({ meetings: meetings as Meeting[], loading: false })
    } catch {
      // Keep whatever is on screen.
      //
      // This used to fall back to `reloadFromRepo()`, and the repo holds none
      // of these — `refreshStatuses` says so a few lines down: "rebuilding from
      // the repo would drop every meeting that came from the API, which is all
      // of them". So one transient failure — a token refreshing mid-flight, a
      // dropped request — emptied the whole list, and the user saw "no
      // meetings" for meetings that plainly existed a second earlier.
      //
      // A failed refresh should leave the last good answer alone, not replace
      // it with a worse one.
      set({ loading: false })
    }
  },

  fetchRange: async ({ from, to }) => {
    try {
      const { meetings: fetched } = await meetingsApi.list({ from, to })
      const fetchedIds = new Set((fetched as Meeting[]).map((m) => m.id))
      set((state) => {
        const kept = state.meetings.filter((m) => !fetchedIds.has(m.id))
        return { meetings: [...(fetched as Meeting[]), ...kept] }
      })
    } catch {
      // Silent — calendar already shows whatever was in the store before.
    }
  },

  addMeeting: (meeting) => {
    if (!meetingsRepo.findById(meeting.id)) {
      const safe = withDefaults(meeting)
      meetingsRepo.insert(safe)
      // Prepend to what is already on screen. Rebuilding from the repo here
      // would have dropped every API-loaded meeting, exactly as it did on
      // update — the new meeting would appear and all the others vanish.
      set((state) => ({ meetings: [withStatus(safe), ...state.meetings] }))
      return
    }
    set((state) => ({
      meetings: state.meetings.map((m) =>
        sameMeeting(m, meeting.id) ? withStatus({ ...m, ...meeting }) : m,
      ),
    }))
  },

  /**
   * A guest's meeting: on this page, never in the browser's stored list.
   *
   * The meeting screen needs its title, host and settings, so the guest join
   * seeds them here. It went through `addMeeting`, which saved the preview to
   * localStorage — no start time, no attendees, and not even the guest's own
   * meeting. Sign-out left it there, and the next sign-in in that browser read
   * it back onto the dashboard as one of today's meetings, which crashed the
   * meeting list (BUG-000533). Nothing reads the stored copy for a guest: after
   * a reload the guest's Join click admits them again and seeds it again.
   */
  seedMeeting: (meeting) => {
    const seeded = withStatus(withDefaults(meeting))
    set((state) => ({
      meetings: [seeded, ...state.meetings.filter((m) => !sameMeeting(m, meeting.id))],
    }))
  },

  clear: () => {
    meetingsRepo.clear()
    set({ meetings: [] })
  },

  // Patch what is on screen; persist locally as well.
  //
  // These used to write to the repo and then rebuild the whole list from it —
  // `set({ meetings: reloadFromRepo() })`. The repo is localStorage, and
  // `fetchFromApi` never writes to it: the dashboard's meetings live in this
  // store and nowhere else. So editing one meeting replaced every meeting on
  // screen with whatever localStorage happened to hold, which for an
  // API-loaded dashboard is nothing — the list emptied until a reload put it
  // back. The repo write stays, best-effort, for the locally created meetings
  // that do live there.
  updateMeeting: (id, patch) => {
    meetingsRepo.update(id, patch)
    set((state) => ({
      meetings: state.meetings.map((m) =>
        sameMeeting(m, id) ? withStatus({ ...m, ...patch }) : m,
      ),
    }))
  },

  updateMeetingStatus: (id, status) => {
    meetingsRepo.update(id, { status })
    // The caller named a status, so it is used as given rather than derived —
    // 'cancelled' is not something the clock can work out.
    set((state) => ({
      meetings: state.meetings.map((m) => (sameMeeting(m, id) ? { ...m, status } : m)),
    }))
  },

  removeMeeting: (id) => {
    meetingsService.remove(id)
    set((state) => ({ meetings: state.meetings.filter((m) => !sameMeeting(m, id)) }))
  },

  /**
   * Delete a meeting for real.
   *
   * `removeMeeting` above only ever touched this client: the repo write and
   * the store filter. The dashboard's meetings come from the API and the repo
   * holds none of them, so deleting one made it vanish until the next fetch
   * put it straight back — the meeting was never deleted, it was hidden for a
   * few seconds.
   *
   * The server call goes first. Dropping it from the list before knowing the
   * delete succeeded is how you get a meeting that is gone from the screen and
   * still in everybody's calendar.
   */
  deleteMeeting: async (id, scope = 'one') => {
    await meetingsApi.delete(id, { scope })
    if (scope === 'all') {
      // Every upcoming day of the series changed, not just this row: reload.
      await get().fetchFromApi()
      return
    }
    meetingsService.remove(id)
    set((state) => ({ meetings: state.meetings.filter((m) => !sameMeeting(m, id)) }))
  },

  refreshStatuses: () => {
    meetingsService.refreshStatuses()
    // Re-derive over what is on screen. Rebuilding from the repo would drop
    // every meeting that came from the API, which is all of them.
    set((state) => ({ meetings: state.meetings.map(withStatus) }))
  },
}))

/**
 * Sign-out, or a different account signing in, empties the meetings.
 *
 * They outlived the session in both places: localStorage (logout never cleared
 * `inmeet_meetings`) and this store, which a sign-in without a reload kept
 * showing until the next fetch landed. So a browser carried one account's
 * meetings into the next, and a stored guest preview survived to crash the
 * dashboard after the next login (BUG-000533).
 *
 * On the transition only, never on "no user": before the session hydrates
 * there is no user either, and clearing then would wipe the list on every load.
 */
useAuthStore.subscribe((state, prev) => {
  if (prev.user && prev.user.id !== state.user?.id) useMeetingsStore.getState().clear()
})

/** Selector — guards against stale status by re-resolving on each read. */
export const selectMeetingById =
  (id: string | undefined) =>
  (s: MeetingsState): Meeting | undefined => {
    if (!id) return undefined
    return s.meetings.find((m) => sameMeeting(m, id))
  }
